/**
 * Autofill the cart and submit the final order on staff intervention
 * (HITL-1551).
 *
 * WHY THIS IS THIN. It fills the CART, then calls the ordinary `sendOrder`.
 * There is no parallel submit path: the earlier draft of this ticket forked one
 * to dodge `sendOrder`'s `getValidCartItems` filter, and paid for it by having to
 * re-implement order-metadata assembly and by silently dropping
 * `dispatchOrderSideEffects` (coupon/loyalty registration and the COUPON_ADDED
 * metric), `populateOrderRequests` (so the returning POS `check` had no request
 * to attach to) and `sendOrderMetrics`. Filling the cart instead makes the items
 * valid, so the filter keeps them and all of that comes along for free.
 *
 * ON `sendOrder`'s `isAIAutoMode` GUARD. Nothing is bypassed. `isAIAutoMode` goes
 * false in exactly one place — `messagingActions.sendAgentInterception`
 * (ai.slice.ts:152) — and a staff intervention dispatches that interception
 * itself, from `cancelOrder`. This runs after that dispatch, which is the same
 * ordering `finishOrder` relies on (OrderActions.tsx:430) and the reason the
 * guard never blocks a normal Finish Order either.
 *
 * ORDERING IS LOAD-BEARING for a second reason: `cancelOrder` ends with
 * `setFinalOrderSubmitted(true)`, whose effect resets the cart. `selectModifiers`
 * is a plain reducer and `sendOrder` snapshots state before its first `await`, so
 * both must be dispatched before that — see the call site.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { cartActions } from './cartSlice';
import { orderActions, sendOrder } from './orderSlice';
import { buildCartAutofillSelections } from '../utils/cartAutofill';
import { convertToMapUsingName } from '../utils';
import logger from '../utils/logger';
import { messagingActions } from './messagingSlice';

/** Stable New Relic token — count on this, not on the prose. */
export const AUTOFILL_FINAL_ORDER_LOG_TOKEN = 'AUTOFILL_FINAL_ORDER';
export const AUTOFILL_FINAL_ORDER_METRIC = 'AUTOFILL_FINAL_ORDER_METRIC';
export const AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION = '2.0';

/**
 * What the attempt did. There is no agent-facing surface on this path — the
 * session is torn down moments later — so this metric IS the surface, both in
 * New Relic (`module = 'HITL'`) and on the event stream.
 */
export enum AutofillFinalOrderOutcome {
  /** Slots were filled, and the order was handed to `sendOrder`. */
  filledAndSubmitted = 'filled_and_submitted',
  /** Nothing needed filling; the order was submitted as it stood. */
  submittedUnchanged = 'submitted_unchanged',
  /** At least one required slot had no fillable option. Submitted anyway —
   * `sendOrder` drops only the still-invalid items, so the rest of the cart
   * still reaches staff, which beats sending nothing. */
  submittedIncomplete = 'submitted_incomplete',
  /** The kill switch is off. */
  disabled = 'disabled',
}

export interface IAutofillFinalOrderResult {
  outcome: AutofillFinalOrderOutcome;
  /** One `modGroup=item` entry per fill — the "fills are logged" criterion. */
  fills: string[];
  /** Required groups the ladder could not fill. */
  unfillable: string[];
}

export interface IAutofillFinalOrderArgs {
  /** The same handlers interim orders use, so a failed FINAL submit surfaces
   * through the menu-module error UI. Threaded from the call site rather than
   * rebuilt here. */
  menuUnificationOrderFailureCallback?: Function;
  menuUnificationOrderSuccessCallback?: Function;
}

export const autofillAndSubmitFinalOrder = createAsyncThunk<
  IAutofillFinalOrderResult,
  IAutofillFinalOrderArgs,
  { state: any }
>(
  'order/autofillAndSubmitFinalOrder',
  async (
    {
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
    },
    { dispatch, getState }
  ) => {
    const {
      config: {
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION,
        FEATURE_FLAG_AUTO_COMBO,
        FEATURE_FLAG_BM_AUTO_COMBO,
        FEATURE_FLAG_SUPPRESS_UPSELL,
      },
      menu: { brandMenu, modSymbolMapping, menuRes, unavailableItemsByName },
      cart: { cartItems },
      autoCombo: { autoCombinedCartItems },
      restaurant,
      sessionBoundary: { sessionId },
    } = getState();

    const restaurantCode =
      restaurant?.selectedRestaurantDetails?.restaurantCode || '';

    if (!FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION) {
      return {
        outcome: AutofillFinalOrderOutcome.disabled,
        fills: [],
        unfillable: [],
      };
    }

    // `sendOrder` picks the auto-combined cart when auto-combo is on, so the
    // fill has to work on the same one or it completes items that never ship.
    const autoCombined =
      (FEATURE_FLAG_AUTO_COMBO || FEATURE_FLAG_BM_AUTO_COMBO) &&
      Object.values(autoCombinedCartItems ?? {}).length;
    const sourceCartItems = autoCombined ? autoCombinedCartItems : cartItems;

    const { selections, fills, unfillable } = buildCartAutofillSelections(
      sourceCartItems,
      {
        brandMenuConstraints: convertToMapUsingName(
          brandMenu?.constraints ?? []
        ),
        modSymbolMapping: modSymbolMapping ?? {},
        posSettings: menuRes?.posSettings ?? {},
        suppressUpsellEnabled: FEATURE_FLAG_SUPPRESS_UPSELL,
      }
    );

    // MUST be set before the fill lands. Filling flips items from invalid to
    // valid, so `selectValidCartItems` changes and OrderReview's cart-change
    // effect (OrderReview.tsx:105) fires — sending an interim order right behind
    // our final one, and bumping `menuModuleRequestSeqId` so the final's own
    // callbacks are gated out as stale. `sendOrder` drops non-final sends while
    // this is set.
    dispatch(orderActions.setIsAutofillFinalInFlight(true));

    try {
      if (selections.length) {
        dispatch(
          cartActions.selectModifiers({ selections, unavailableItemsByName })
        );
      }

      const outcome = unfillable.length
        ? AutofillFinalOrderOutcome.submittedIncomplete
        : fills.length
        ? AutofillFinalOrderOutcome.filledAndSubmitted
        : AutofillFinalOrderOutcome.submittedUnchanged;

      const result: IAutofillFinalOrderResult = { outcome, fills, unfillable };
      const logPayload = {
        message: `${AUTOFILL_FINAL_ORDER_LOG_TOKEN}: ${outcome}`,
        sessionId,
        restaurantCode,
        moreInfo: { fills, fillCount: fills.length, unfillable },
      };
      if (unfillable.length) {
        logger.warn(logPayload);
      } else {
        logger.info(logPayload);
      }

      dispatch(
        messagingActions.sendInfo({
          data: {
            type: AUTOFILL_FINAL_ORDER_METRIC,
            schema_version: AUTOFILL_FINAL_ORDER_METRIC_SCHEMA_VERSION,
            outcome,
            fills,
            fill_count: fills.length,
            unfillable,
          },
        } as any)
      );

      // The cart is now as complete as it can be made. Everything from here is
      // the ordinary final-order path.
      // Cast: this thunk is typed `{ state: any }` (the store's RootState is not
      // importable here without a cycle), which loosens thunkAPI's dispatch past
      // what a nested thunk's signature wants.
      await (dispatch as any)(
        sendOrder({
          isFinal: true,
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );

      return result;
    } finally {
      // The suppression window closes as soon as the final send has been made.
      // In `finally` so a throw cannot strand it on — leaving it set would mute
      // every interim order for the rest of the session.
      dispatch(orderActions.setIsAutofillFinalInFlight(false));
    }
  }
);
